// components/Header.tsx
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  MapPin, User, ShoppingCart, Grid3x3, Home, Search,
  ChevronDown, Menu, X, Heart, Package, LogOut, Bell
} from "lucide-react";
import { NavLink } from "../../ui/NavLink";
import { useAuth, useFeaturedCategories } from "@cabukaka/hooks";
import { useCart } from "../../../hooks/cart/useCart";

const menuItems = [
  { href: "/", label: "Accueil", icon: Home },
  { href: "/categories", label: "Catégories", icon: Grid3x3 },
  { href: "/panier", label: "Panier", icon: ShoppingCart },
  { href: "/compte", label: "Mon compte", icon: User },
];

const drawerMenuItems = [
  { href: "/", label: "Accueil", icon: Home },
  { href: "/categories", label: "Catégories", icon: Grid3x3 },
  { href: "/panier", label: "Mon panier", icon: ShoppingCart },
  { href: "/compte/favoris", label: "Mes favoris", icon: Heart },
  { href: "/compte/commandes", label: "Mes commandes", icon: Package },
  { href: "/compte", label: "Mon compte", icon: User },
];



const cities = ["Bunia", "Butembo"];

const Header = () => {
  const [selectedCity, setSelectedCity] = useState("");
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCategory, setSearchCategory] = useState("all");
  const { itemCount } = useCart()
  const { featuredCategories } = useFeaturedCategories()

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth(); // Récupération de l'utilisateur connecté via ton hook
  const isLoggedIn = !!user;

  useEffect(() => {
    const savedCity = localStorage.getItem("selectedCity");
    if (savedCity) {
      setSelectedCity(savedCity);
    } else if (cities.length > 0) {
      setSelectedCity(cities[0]!);
    }
  }, []);

  const handleCitySelect = (city: string) => {
    setSelectedCity(city);
    localStorage.setItem("selectedCity", city);
    setShowCityDropdown(false);
  };

  const handleSearch = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("q", searchQuery.trim());
    if (searchCategory !== "all") params.set("q", searchCategory);

    const queryString = params.toString();
    navigate(queryString ? `/products?${queryString}` : "/products");

    setShowSearchModal(false);
  };

  return (
    <>
      {/* HEADER GLOBAL */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm transition-all duration-300">



        {/* ==================== DESKTOP MAIN BAR (Recherche + Compte) ==================== */}
        <div className="hidden md:flex items-center justify-between gap-6 px-6 py-4 max-w-[1400px] mx-auto">
          {/* Logo */}
          <NavLink to="/" className="flex-shrink-0">
            <img src="/logo.png" alt="Cabukaka" className="h-[40px] w-auto object-contain" />
          </NavLink>

          {/* Barre de Recherche Alibaba-Style */}
          <form onSubmit={handleSearch} className="flex-1 max-w-2xl flex items-center border-2 border-primary rounded-full overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
            <div className="relative flex-shrink-0 border-r border-gray-200">
              <select
                value={searchCategory}
                onChange={(e) => setSearchCategory(e.target.value)}
                className="pl-4 pr-8 py-2 bg-gray-50 text-gray-700 text-sm font-medium focus:outline-none appearance-none cursor-pointer h-10"
              >
                {featuredCategories.map((cat) => (
                  <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>

            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Je cherche des articles, des marques ou des boutiques..."
                className="w-full pl-4 pr-10 py-2 text-sm text-gray-800 focus:outline-none placeholder-gray-400"
              />
            </div>

            <button
              type="submit"
              className="bg-primary text-white px-6 h-10 font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              <span className="text-sm hidden lg:inline">Rechercher</span>
            </button>
          </form>

          {/* Actions Utilisateur */}
          <div className="flex items-center gap-6">
            {/* Profil */}
            <NavLink
              to={isLoggedIn ? "/compte" : "/auth/login"}
              className="flex items-center gap-2.5 text-gray-700 hover:text-primary transition-colors group"
            >
              <div className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <User className="w-5 h-5 text-gray-600 group-hover:text-primary" />
              </div>
              <div className="text-left leading-tight hidden lg:block">
                <p className="text-xs text-gray-400">Bonjour,</p>
                <p className="text-sm font-semibold text-gray-800 max-w-[120px] truncate">
                  {isLoggedIn ? (user?.username || "Mon compte") : "Se connecter"}
                </p>
              </div>
            </NavLink>

            {/* Favoris */}
            <NavLink to="/compte/favoris" className="relative p-2 text-gray-600 hover:text-primary transition-colors">
              <Heart className="w-6 h-6" />
            </NavLink>

            {/* Panier */}
            <NavLink to="/panier" className="relative flex items-center gap-2 p-2 bg-gray-50 hover:bg-primary/5 rounded-xl transition-colors text-gray-700 hover:text-primary group">
              <div className="relative">
                <ShoppingCart className="w-6 h-6 text-gray-600 group-hover:text-primary" />
                <span className="absolute -top-2 -right-2 bg-primary text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              </div>
              <span className="text-sm font-semibold hidden lg:inline">Panier</span>
            </NavLink>
          </div>
        </div>

        {/* ==================== DESKTOP BOTTOM BAR (Navigation) ==================== */}
        <div className="hidden md:flex border-t border-gray-100 px-6 py-2.5 max-w-[1400px] mx-auto items-center justify-between text-sm">
          <div className="flex items-center gap-8 font-medium text-gray-700">
            {menuItems.map((item) => {
              const isActive = pathname === item.href;
              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={`hover:text-primary transition-colors pb-1 border-b-2 ${isActive ? "text-primary border-primary" : "border-transparent"
                    }`}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </div>
          <div className="relative">
            <button onClick={() => setShowCityDropdown(!showCityDropdown)} className="flex items-center gap-2 text-sm font-semibold text-primary bg-primary/10 px-4 py-1.5 rounded-full hover:bg-primary/20 transition-colors">
              <MapPin className="w-4 h-4" />
              {selectedCity}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showCityDropdown && (
              <div className="absolute top-full right-0 mt-2 bg-white rounded-xl shadow-xl border w-40 py-1 z-50">
                {cities.map((city) => (
                  <button key={city} onClick={() => handleCitySelect(city)} className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-50">{city}</button>
                ))}
              </div>
            )}
          </div>
        </div>


        {/* ==================== MOBILE HEADER ==================== */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 md:hidden">
          <button
            onClick={() => setShowMobileMenu(true)}
            className="p-2 -ml-2 text-gray-700 active:bg-gray-100 rounded-xl"
            aria-label="Menu"
          >
            <Menu className="w-6 h-6" />
          </button>

          <NavLink to="/" className="flex-1 flex justify-center">
            <img src="/logo.png" alt="Cabukaka" className="h-8 w-auto object-contain" />
          </NavLink>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowSearchModal(true)}
              className="p-2 text-gray-700 active:bg-gray-100 rounded-xl"
              aria-label="Rechercher"
            >
              <Search className="w-5 h-5" />
            </button>
            <NavLink to="/compte" className="p-2 text-gray-700 active:bg-gray-100 rounded-xl">
              <User className="w-5 h-5" />
            </NavLink>
          </div>
        </div>
      </header>

      {/* ==================== BOTTOM NAVIGATION BAR MOBILE ==================== */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 md:hidden shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <div className="flex justify-around items-center h-16">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = pathname === item.href;
            return (
              <NavLink
                key={item.href}
                to={item.href}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${isActive ? "text-primary font-medium" : "text-gray-500"
                  }`}
              >
                <Icon className={`w-5 h-5 ${isActive ? "stroke-[2.5px]" : "stroke-[2px]"}`} />
                <span className="text-[10px] mt-1">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* ==================== DRAWER MOBILE ==================== */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={() => setShowMobileMenu(false)}></div>
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white shadow-2xl flex flex-col animate-in slide-in-from-left duration-200">

            {/* Drawer Header */}
            <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50">
              <NavLink to="/" onClick={() => setShowMobileMenu(false)}>
                <img src="/logo.png" alt="Cabukaka" className="h-8 w-auto object-contain" />
              </NavLink>
              <button onClick={() => setShowMobileMenu(false)} className="p-2 text-gray-500 rounded-lg hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* User Account Banner */}
            <div className="p-4 border-b border-gray-100">
              {isLoggedIn ? (
                <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-xl">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-semibold text-sm text-gray-800 truncate">{user?.username || "Utilisateur"}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email || ""}</p>
                  </div>
                </div>
              ) : (
                <NavLink
                  to="/auth/login"
                  onClick={() => setShowMobileMenu(false)}
                  className="block w-full bg-primary text-white text-center py-2.5 rounded-xl text-sm font-semibold shadow-sm hover:bg-primary/90 transition-colors"
                >
                  S'inscrire / Se connecter
                </NavLink>
              )}
            </div>

            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
              {drawerMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = pathname === item.href;
                return (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    onClick={() => setShowMobileMenu(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm ${isActive ? "bg-primary/10 text-primary font-semibold" : "text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    <Icon className="w-5 h-5 opacity-80" />
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50">
              {isLoggedIn && (
                <button
                  onClick={() => {
                    logout(); // Appel de la fonction de déconnexion native
                    setShowMobileMenu(false);
                  }}
                  className="flex items-center gap-3 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-xl transition-colors w-full text-sm font-medium"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Se déconnecter</span>
                </button>
              )}
              <p className="text-center text-[10px] text-gray-400 mt-2">Cabukaka v1.0.0</p>
            </div>
          </div>
        </div>
      )}

      {/* ==================== MODAL RECHERCHE MOBILE ==================== */}
      {showSearchModal && (
        <div className="fixed inset-0 z-50 bg-white md:hidden animate-in fade-in duration-150">
          <div className="p-4 flex flex-col h-full">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => setShowSearchModal(false)} className="p-2 -ml-2 text-gray-700">
                <X className="w-6 h-6" />
              </button>
              <span className="font-bold text-lg text-gray-800">Rechercher</span>
            </div>

            <form onSubmit={handleSearch} className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Que recherchez-vous aujourd'hui ?"
                  className="flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 text-sm"
                  autoFocus
                />
                <button
                  type="submit"
                  className="bg-primary text-white p-3 rounded-xl hover:bg-primary/90 transition-colors"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Filtrer par Catégorie</label>
                <select
                  value={searchCategory}
                  onChange={(e) => setSearchCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-primary text-sm appearance-none cursor-pointer"
                >
                  {featuredCategories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ESPACE COMPENSATEUR DU HEADER FIXE */}
      <div style={{ paddingTop: 0, paddingBottom: '40px' }} className="md:pb-0"></div>
    </>
  );
};

export default Header;